import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { db } from '../config/firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+82', country: 'S. Korea', flag: '🇰🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'Norway', flag: '🇳🇴' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
];

/**
 * ProfileOnboarding - Initial profile setup for new users
 * Shows after signup to collect: firstName, lastName, displayName, mobile, privacy
 */
export default function ProfileOnboarding({ onComplete }) {
  const { user, userProfile } = useAuth();
  const toast = useToast();

  const [step, setStep] = useState(1); // 1: Names, 2: Display Name, 3: Mobile, 4: Privacy
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    phone: '',
    countryCode: '+1',
    profilePublic: false // Default to PRIVATE
  });

  // Check if profile is already complete
  useEffect(() => {
    if (userProfile?.firstName && userProfile?.lastName && userProfile?.displayName) {
      onComplete?.();
    }
  }, [userProfile, onComplete]);

  const handleNextStep = () => {
    if (step === 1) {
      // Validate first & last name
      if (formData.firstName.trim().length < 2) {
        toast.error('First name must be at least 2 characters');
        return;
      }
      if (formData.lastName.trim().length < 2) {
        toast.error('Last name must be at least 2 characters');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      // Validate display name
      if (formData.displayName.trim().length < 2) {
        toast.error('Display name must be at least 2 characters');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      // Mobile is optional, go to privacy
      setStep(4);
    }
  };

  const handlePrevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const profileRef = doc(db, 'users', user.uid);
      await setDoc(profileRef, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        displayName: formData.displayName.trim(),
        phone: formData.phone.trim(),
        countryCode: formData.countryCode,
        profilePublic: formData.profilePublic,
        updatedAt: serverTimestamp()
      }, { merge: true });

      toast.success('Profile setup complete!');
      onComplete?.();
    } catch (error) {
      console.error('Error completing profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#1c1c1e] rounded-2xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="px-6 py-8 border-b border-[#d2d2d7] dark:border-[#2c2c2e]">
          <h1 className="text-2xl font-bold text-[#1d1d1f] dark:text-white">
            Welcome to QuickMeet
          </h1>
          <p className="text-sm text-[#86868b] dark:text-[#6e6e73] mt-2">
            Step {step} of 4 • Let's set up your profile
          </p>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-[#f5f5f7] dark:bg-[#2c2c2e]">
          <div
            className="h-full bg-gradient-to-r from-[#06c] to-[#0055d4] transition-all duration-300"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="px-6 py-8 min-h-[300px] flex flex-col">
          {/* Step 1: First & Last Name */}
          {step === 1 && (
            <div className="space-y-4 flex-1">
              <div>
                <label className="block text-sm font-semibold text-[#1d1d1f] dark:text-white mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="John"
                  autoFocus
                  className="w-full px-4 py-2.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1d1d1f] dark:text-white mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Doe"
                  className="w-full px-4 py-2.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all text-sm"
                />
              </div>
            </div>
          )}

          {/* Step 2: Display Name */}
          {step === 2 && (
            <div className="space-y-4 flex-1">
              <div>
                <p className="text-sm text-[#86868b] dark:text-[#6e6e73] mb-4">
                  How would you like to appear to others in meetings and chat?
                </p>
                <label className="block text-sm font-semibold text-[#1d1d1f] dark:text-white mb-2">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                  placeholder={`${formData.firstName} ${formData.lastName}`.trim() || 'Your Display Name'}
                  autoFocus
                  className="w-full px-4 py-2.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all text-sm"
                />
                <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-2">
                  Can be your full name, nickname, or initials (e.g., "JohnD", "Johnny", "J.D.")
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Mobile Number */}
          {step === 3 && (
            <div className="space-y-4 flex-1">
              <div>
                <p className="text-sm text-[#86868b] dark:text-[#6e6e73] mb-4">
                  Add your phone number (optional)
                </p>
                <label className="block text-sm font-semibold text-[#1d1d1f] dark:text-white mb-2">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, countryCode: e.target.value }))}
                    className="px-3 py-2.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all text-sm w-32"
                  >
                    {COUNTRY_CODES.map(({ code, country, flag }) => (
                      <option key={code} value={code}>
                        {flag} {code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="123 456 7890"
                    autoFocus
                    className="flex-1 px-4 py-2.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Privacy Settings */}
          {step === 4 && (
            <div className="space-y-4 flex-1">
              <div>
                <h3 className="text-sm font-semibold text-[#1d1d1f] dark:text-white mb-4">
                  Privacy Settings
                </h3>

                {/* Private Option (Default) */}
                <button
                  onClick={() => setFormData(prev => ({ ...prev, profilePublic: false }))}
                  className={`w-full p-4 rounded-lg border-2 transition-all mb-3 ${
                    !formData.profilePublic
                      ? 'border-[#06c] bg-[#06c]/10'
                      : 'border-[#d2d2d7] dark:border-[#2c2c2e] hover:border-[#06c]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        !formData.profilePublic
                          ? 'border-[#06c] bg-[#06c]'
                          : 'border-[#d2d2d7] dark:border-[#6e6e73]'
                      }`}>
                        {!formData.profilePublic && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-semibold text-[#1d1d1f] dark:text-white">
                        🔒 Private (Recommended)
                      </p>
                      <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-1">
                        Share a contact code with people to add you
                      </p>
                    </div>
                  </div>
                </button>

                {/* Public Option */}
                <button
                  onClick={() => setFormData(prev => ({ ...prev, profilePublic: true }))}
                  className={`w-full p-4 rounded-lg border-2 transition-all ${
                    formData.profilePublic
                      ? 'border-[#06c] bg-[#06c]/10'
                      : 'border-[#d2d2d7] dark:border-[#2c2c2e] hover:border-[#06c]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.profilePublic
                          ? 'border-[#06c] bg-[#06c]'
                          : 'border-[#d2d2d7] dark:border-[#6e6e73]'
                      }`}>
                        {formData.profilePublic && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-semibold text-[#1d1d1f] dark:text-white">
                        🌐 Public
                      </p>
                      <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-1">
                        Anyone can find you by searching name, email, or phone
                      </p>
                    </div>
                  </div>
                </button>

                <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-4">
                  You can change this later in Settings → Privacy & Contacts
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="px-6 py-6 border-t border-[#d2d2d7] dark:border-[#2c2c2e] flex gap-3">
          {step > 1 && (
            <button
              onClick={handlePrevStep}
              disabled={saving}
              className="flex-1 py-2.5 px-4 border border-[#d2d2d7] dark:border-[#2c2c2e] rounded-lg text-[#1d1d1f] dark:text-white font-medium hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-colors disabled:opacity-50"
            >
              Back
            </button>
          )}

          {step < 4 ? (
            <button
              onClick={handleNextStep}
              disabled={saving}
              className="flex-1 py-2.5 px-4 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleComplete}
              disabled={saving}
              className="flex-1 py-2.5 px-4 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50"
            >
              {saving ? 'Setting up...' : 'Complete Setup'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
